import { useState } from "react";
import { LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import type { FileRelatedType } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { cn } from "@/lib/cn";
import { useSoLeitura } from "@/components/organization/painel/OrganizacaoEmFoco";
import { FilesDropzone } from "./FilesDropzone";
import { EntityFilesView } from "./EntityFilesView";
import { RenameDialog } from "./RenameDialog";
import { MoveDialog, type MoveTarget } from "./MoveDialog";
import { FileTagsDialog } from "./FileTagsDialog";
import { ShareDialog } from "./ShareDialog";
import { FileHistoryDialog } from "./FileHistoryDialog";
import { FilePreviewDialog } from "./FilePreviewDialog";
import type { FileRow } from "./files-types";

/** Painel de arquivos reutilizável em qualquer aba "Arquivos" de entidade (Paciente, Pedido,
 *  Serviço, Parceiro) — upload com anexo automático à entidade + a mesma listagem agrupada por
 *  tag da tela principal de Arquivos, com renomear/mover/baixar/excluir. */
export function EntityFilesPanel({ relatedType, relatedId }: { relatedType: FileRelatedType; relatedId: string }) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const soLeitura = useSoLeitura();
  const [viewMode, setViewMode] = useState<"grade" | "lista">("grade");
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string; kind: "arquivo" } | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget>(null);
  const [tagsTargetId, setTagsTargetId] = useState<string | null>(null);
  const [shareTargetId, setShareTargetId] = useState<string | null>(null);
  const [historyTargetId, setHistoryTargetId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FileRow | null>(null);

  const invalidateAll = () => utils.file.invalidate();

  const attachToEntity = trpc.file.attachToEntity.useMutation({ onSuccess: invalidateAll });
  const renameFile = trpc.file.rename.useMutation({
    onSuccess: () => {
      toast.success("Arquivo renomeado.");
      setRenameTarget(null);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao renomear arquivo."),
  });
  const moveFile = trpc.file.move.useMutation({
    onSuccess: () => {
      toast.success("Arquivo movido.");
      setMoveTarget(null);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao mover arquivo."),
  });
  const deleteFile = trpc.file.delete.useMutation({
    onSuccess: () => {
      toast.success("Arquivo excluído.");
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir arquivo."),
  });

  async function handleDownload(file: FileRow) {
    try {
      const { url } = await utils.client.file.getDownloadUrl.query({ fileId: file.id });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    }
  }

  async function handleDelete(file: FileRow) {
    const ok = await confirm({
      title: "Excluir arquivo?",
      description: `"${file.name}" será excluído permanentemente. Essa ação não tem volta.`,
      confirmText: "Excluir",
      destructive: true,
    });
    if (ok) deleteFile.mutate({ fileId: file.id });
  }

  return (
    <div>
      {!soLeitura && (
        <FilesDropzone
          kind="GENERIC"
          onUploaded={(file) => attachToEntity.mutate({ fileId: file.fileId, relatedType, relatedId })}
        />
      )}

      <div className="mt-4 mb-2 flex justify-end">
        <div className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
          <button
            type="button"
            onClick={() => setViewMode("grade")}
            aria-label="Visualizar em grade"
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors",
              viewMode === "grade"
                ? "bg-brand-600 font-semibold text-white"
                : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Grade
          </button>
          <button
            type="button"
            onClick={() => setViewMode("lista")}
            aria-label="Visualizar em lista"
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors",
              viewMode === "lista"
                ? "bg-brand-600 font-semibold text-white"
                : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            <List className="h-3.5 w-3.5" />
            Lista
          </button>
        </div>
      </div>

      <EntityFilesView
        relatedType={relatedType}
        relatedId={relatedId}
        viewMode={viewMode}
        onOpenFile={setPreviewFile}
        onDownloadFile={handleDownload}
        onTagsFile={(f) => setTagsTargetId(f.id)}
        onShareFile={(f) => setShareTargetId(f.id)}
        onHistoryFile={(f) => setHistoryTargetId(f.id)}
        onRenameFile={(f) => setRenameTarget({ id: f.id, name: f.name, kind: "arquivo" })}
        onMoveFile={(f) => setMoveTarget({ id: f.id, name: f.name, kind: "arquivo" })}
        onDeleteFile={handleDelete}
      />

      <RenameDialog
        target={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRename={(name) => renameTarget && renameFile.mutate({ fileId: renameTarget.id, name })}
        isPending={renameFile.isPending}
      />
      <MoveDialog
        target={moveTarget}
        onClose={() => setMoveTarget(null)}
        onMove={(folderId) => moveTarget && moveFile.mutate({ fileId: moveTarget.id, folderId })}
        isPending={moveFile.isPending}
      />
      <FileTagsDialog fileId={tagsTargetId} onClose={() => setTagsTargetId(null)} />
      <ShareDialog fileId={shareTargetId} onClose={() => setShareTargetId(null)} />
      <FileHistoryDialog fileId={historyTargetId} onClose={() => setHistoryTargetId(null)} />
      <FilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
    </div>
  );
}
